import type { AttachmentFolder } from '@/gen/cortado/v1/attachments_pb'

/** A folder plus its resolved children, for rendering the library tree. */
export interface FolderTreeNode {
  folder: AttachmentFolder
  children: FolderTreeNode[]
}

/**
 * Build a folder tree from the backend's flat folder list.
 *
 * Root folders are those with no `parentFolderId`. Children are sorted by name
 * (case-insensitive) at every level. Any folder whose parent is missing from the
 * list is treated as a root so it never disappears from the UI.
 */
export function buildFolderTree(folders: AttachmentFolder[]): FolderTreeNode[] {
  const byParent = new Map<string, AttachmentFolder[]>()
  const ids = new Set(folders.map((f) => f.id))

  for (const folder of folders) {
    const parentKey =
      folder.parentFolderId && ids.has(folder.parentFolderId)
        ? folder.parentFolderId
        : '__root__'
    const list = byParent.get(parentKey) ?? []
    list.push(folder)
    byParent.set(parentKey, list)
  }

  const sortByName = (a: AttachmentFolder, b: AttachmentFolder) =>
    a.name.localeCompare(b.name, undefined, { sensitivity: 'base' })

  const buildLevel = (parentKey: string): FolderTreeNode[] => {
    const level = byParent.get(parentKey) ?? []
    return [...level].sort(sortByName).map((folder) => ({
      folder,
      children: buildLevel(folder.id),
    }))
  }

  return buildLevel('__root__')
}

/**
 * Collect the id of every descendant folder of `folderId` (inclusive), so a
 * "move folder" UI can disallow re-parenting a folder under itself or its own
 * subtree.
 */
export function collectDescendantFolderIds(
  folders: AttachmentFolder[],
  folderId: string
): Set<string> {
  const byParent = new Map<string, AttachmentFolder[]>()
  for (const folder of folders) {
    if (!folder.parentFolderId) continue
    const list = byParent.get(folder.parentFolderId) ?? []
    list.push(folder)
    byParent.set(folder.parentFolderId, list)
  }

  const result = new Set<string>([folderId])
  const stack = [folderId]
  while (stack.length > 0) {
    const current = stack.pop()!
    for (const child of byParent.get(current) ?? []) {
      if (!result.has(child.id)) {
        result.add(child.id)
        stack.push(child.id)
      }
    }
  }
  return result
}
